import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { addMonths, addQuarters, addYears } from 'date-fns'
import {
  ButtonV2,
  DatePickerV2,
  InputStartAdornment,
  RadioButtonGroupMapping,
  NoteV2,
  RadioButtonsV2,
  TextInputV4,
} from '@labrav/react-components'

import { get, isUndefined } from 'lodash'

import { useStyles } from './CombinedFormPaymentSchedule.styles'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'

import { isNonEmptyNumber } from '@labrav/common'
import { InstallmentInfo } from '../../../../types/flyout'
import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { ScheduleType } from '../../../../modules/privateOffer/reducer'
import { getRandom } from '../../../../common/utils/helperFunctions'
import { getScheduleTypeOptions } from '../../../../common/utils/productCreationUtil'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { labraUIDateFormatForm } from '../OfferDetails/OfferDetails'
import { DateTime } from 'luxon'
import { getCappedScheduledInstallmentCount } from '../../../../common/utils/constants'
import { awsMonthlyInstallmentsBannerMessage } from '../../../../common/utils/messagesContants'

interface CustomColumnInstallment {
  keyName: string
  header: string
}
interface PaymentScheduleV2Props {
  formik: FormikProps<Record<string, unknown>>
  subHeading?: React.ReactNode
  addButtonName: string
  buttonCounterName: string
  shouldShowTotal?: boolean
  installmentsLimit: number
  sectionDescription?: string
  sectionHeading?: string
  tcvKey?: string
  tcv?: number
  fixTcvView?: boolean
  handleChangeInstallmentsAmount?: (installents: InstallmentInfo[]) => void
  customColumns?: CustomColumnInstallment[]
  totalOtherAmount?: number
  updatedRandomKey?: number
  buyerTcv?: number
  disabled: boolean
}

export const CombinedFormPaymentSchedule = ({
  formik,
  subHeading,
  addButtonName,
  buttonCounterName,
  installmentsLimit,
  sectionHeading = 'Installment schedule',
  sectionDescription = 'Generate installment plan for the buyer based on total contract value and duration.',
  tcvKey = 'tcv',
  tcv,
  buyerTcv = 0,
  handleChangeInstallmentsAmount,
  customColumns = [],
  totalOtherAmount,
  updatedRandomKey,
  disabled,
}: PaymentScheduleV2Props) => {
  const { classes } = useStyles()
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    handleBlur,
    errors,
  } = useFormikContext() as FormikProps<CombinedFormState>
  const currencySymbol =
    getCurrencySymbol(values?.resellerAgreementData.currency as string) || '$'
  const tcvValue = !isUndefined(tcv) ? tcv : get(values, tcvKey, 0)
  const installmentsLength = values.resellerAgreementData.installmentInfo.length
  const emptyResourse = { amount: null, paymentDate: '', currency: 'USD' }
  const newDate = addOneDayToDate(values.cppoData.offerExpirationDate as string)
  const [randomKey, setRandomKey] = useState<number>(getRandom())
  useEffect(() => {
    setRandomKey(updatedRandomKey || getRandom())
  }, [updatedRandomKey])

  const scheduleTypeOptions: RadioButtonGroupMapping[] =
    getScheduleTypeOptions(disabled)

  const checkedValue = useMemo(
    () =>
      values.cppoData.flexiblePaymentType === ScheduleType.QUARTERLY
        ? 'Quarterly'
        : values.cppoData.flexiblePaymentType === ScheduleType.MONTHLY
          ? 'Monthly'
          : values.cppoData.flexiblePaymentType === ScheduleType.YEARLY
            ? 'Yearly'
            : 'Custom',
    [values.cppoData.flexiblePaymentType]
  )

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionKey = (event.target as HTMLInputElement).value as string
    if (selectedOptionKey === 'Quarterly') {
      setFieldValue('cppoData.flexiblePaymentType', ScheduleType.QUARTERLY)
    } else if (selectedOptionKey === 'Monthly') {
      setFieldValue('cppoData.flexiblePaymentType', ScheduleType.MONTHLY)
    } else if (selectedOptionKey === 'Yearly') {
      setFieldValue('cppoData.flexiblePaymentType', ScheduleType.YEARLY)
    } else if (selectedOptionKey === 'Custom') {
      setFieldValue('cppoData.flexiblePaymentType', ScheduleType.CUSTOM)
    }
  }

  const calculateNumberOfInstallments = (
    flexiblePaymentType: ScheduleType,
    serviceLength?: number,
    containerServiceLength?: number
  ): number => {
    if (serviceLength) {
      switch (flexiblePaymentType) {
        case ScheduleType.MONTHLY:
          return Math.ceil(serviceLength / 1)
        case ScheduleType.QUARTERLY:
          return Math.ceil(serviceLength / 3)
        case ScheduleType.YEARLY:
          return Math.ceil(serviceLength / 12)
        default:
          return serviceLength
      }
    } else if (containerServiceLength) {
      switch (flexiblePaymentType) {
        case ScheduleType.MONTHLY:
          return Math.ceil(containerServiceLength / 30)
        case ScheduleType.QUARTERLY:
          return Math.ceil(containerServiceLength / 90)
        case ScheduleType.YEARLY:
          return Math.ceil(containerServiceLength / 365)
        default:
          return containerServiceLength
      }
    }
    return 1
  }

  const calculateInstallmentDate = (
    startDate: string,
    flexiblePaymentType: ScheduleType,
    installmentNumber: number
  ): string => {
    let calculatedDate

    switch (flexiblePaymentType) {
      case ScheduleType.MONTHLY:
        calculatedDate = addMonths(new Date(startDate), installmentNumber)
        break
      case ScheduleType.QUARTERLY:
        calculatedDate = addQuarters(new Date(startDate), installmentNumber)
        break
      case ScheduleType.YEARLY:
        calculatedDate = addYears(new Date(startDate), installmentNumber)
        break
      default:
        calculatedDate = new Date(startDate)
    }

    // Format the date as "YYYY-MM-DD"
    const formattedDate = calculatedDate.toISOString().split('T')[0]
    return formattedDate
  }

  const getInstallmentsBasedOnSchedule = (
    tcv: number,
    buyerTcv: number,
    durationInMonths: number,
    now: string
  ) => {
    const quantityOfInstallments = getCappedScheduledInstallmentCount(
      calculateNumberOfInstallments(
        values.cppoData.flexiblePaymentType as ScheduleType,
        durationInMonths
      )
    )

    const resellerBase = Math.ceil((tcv / quantityOfInstallments) * 100) / 100 // multiply and divide by 100 as we want 2 decimal precision eg. 200 / 24 = 8.3333333 -> ceil(8.33333 * 100) = 834 / 100 = 8.34
    const preTotal = resellerBase * (quantityOfInstallments - 1)
    const finalResellerPayment = tcv - preTotal
    const buyerBase = Math.ceil((buyerTcv / quantityOfInstallments) * 100) / 100
    const buyerPreTotal = buyerBase * (quantityOfInstallments - 1)
    const finalBuyerPayment = buyerTcv - buyerPreTotal
    return Array.from({ length: quantityOfInstallments }).map((_, index) => ({
      amount:
        index === quantityOfInstallments - 1
          ? finalResellerPayment.toFixed(2)
          : resellerBase.toFixed(2),
      buyerAmount:
        index === quantityOfInstallments - 1
          ? finalBuyerPayment.toFixed(2)
          : buyerBase.toFixed(2),
      paymentDate: calculateInstallmentDate(
        (values.resellerAgreementData.firstInstallmentDate
          ? values.resellerAgreementData.firstInstallmentDate
          : newDate) as string,
        values.cppoData.flexiblePaymentType as ScheduleType,
        index
      ),
      currency: 'USD',
      ...(isNonEmptyNumber(totalOtherAmount)
        ? {
            otherAmount: (totalOtherAmount! / quantityOfInstallments).toFixed(
              2
            ),
          }
        : {}),
    }))
  }
  const generateInstallments = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const schedule =
      values.cppoData.flexiblePaymentType || ScheduleType.QUARTERLY

    const newTcv = tcvValue

    if (schedule && schedule !== ScheduleType.CUSTOM) {
      const monthsToAdd = values?.resellerAgreementData
        ?.contractDurationInMonthsForEndCustomer
        ? values.resellerAgreementData.contractDurationTimeUnit === 'YEARS'
          ? values?.resellerAgreementData
              ?.contractDurationInMonthsForEndCustomer * 12
          : values?.resellerAgreementData
              ?.contractDurationInMonthsForEndCustomer
        : 0
      const installments = getInstallmentsBasedOnSchedule(
        newTcv,
        buyerTcv,
        monthsToAdd,
        newDate || ''
      )
      const isvInstallments = installments.map(installment => ({
        amount: installment.amount,
        paymentDate: installment.paymentDate,
        currency: 'USD',
      }))
      const buyerInstallments = installments.map(installment => ({
        amount: installment.buyerAmount || 0,
        paymentDate: installment.paymentDate,
        currency: 'USD',
      }))
      await setFieldValue(
        'resellerAgreementData.installmentInfo',
        isvInstallments
      )
      await setFieldValue('cppoData.installmentInfo', buyerInstallments)
      await setFieldTouched('resellerAgreementData.installmentInfo', undefined)
      await setFieldTouched('cppoData.installmentInfo', undefined)
      await setRandomKey(getRandom())
    }
  }

  const columnDivision = 98 / (customColumns?.length + 2)

  const hasCustomColumn = !!customColumns?.length

  const showDelete =
    values.resellerAgreementData.installmentInfo.length > 1 &&
    (addButtonName === 'Add more installments'
      ? values.cppoData.flexiblePaymentType === ScheduleType.CUSTOM
      : true)

  const handleChangeBuyerAmount = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = (event.target as HTMLInputElement).value
      const numericValue = value !== '' ? Number(value) : 0

      // Update the specific installment
      const updatedBuyerInstallments = [...values.cppoData.installmentInfo]
      updatedBuyerInstallments[index].amount = numericValue
      await setFieldValue('cppoData.installmentInfo', updatedBuyerInstallments)

      // Recalculate Buyer TCV
      const newBuyerTcv = updatedBuyerInstallments.reduce(
        (sum, i) => sum + (Number(i.amount) || 0),
        0
      )
      await setFieldValue('cppoData.tcv', newBuyerTcv)

      // Recalculate markup
      const isvTcv = Number(values.resellerAgreementData.tcv || 0)
      const markup =
        isvTcv > 0 && newBuyerTcv > 0
          ? (((newBuyerTcv - isvTcv) / isvTcv) * 100).toFixed(2)
          : 0
      await setFieldValue('cppoData.adjustmentPercentage', markup)

      if (handleChangeInstallmentsAmount) {
        await handleChangeInstallmentsAmount(updatedBuyerInstallments)
      }
    },
    [
      values.cppoData.installmentInfo,
      values.resellerAgreementData.tcv,
      setFieldValue,
    ]
  )

  const handleChangeAmount = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = (event.target as HTMLInputElement).value
      const numericValue = value !== '' ? Number(value) : 0

      // Update the specific installment
      const updatedInstallments = [
        ...values.resellerAgreementData.installmentInfo,
      ]
      updatedInstallments[index].amount = numericValue
      await setFieldValue(
        'resellerAgreementData.installmentInfo',
        updatedInstallments
      )

      // Recalculate ISV total TCV
      const newIsvTcv = updatedInstallments.reduce(
        (sum, i) => sum + (Number(i.amount) || 0),
        0
      )
      await setFieldValue('resellerAgreementData.tcv', newIsvTcv)

      // Recalculate markup based on new totals
      const buyerTcv = Number(values.cppoData.tcv || 0)
      const markup =
        newIsvTcv > 0 && buyerTcv > 0
          ? (((buyerTcv - newIsvTcv) / newIsvTcv) * 100).toFixed(2)
          : 0
      await setFieldValue('cppoData.adjustmentPercentage', markup)

      if (handleChangeInstallmentsAmount) {
        await handleChangeInstallmentsAmount(updatedInstallments)
      }
      setFieldTouched(`cppoData.installmentInfo.${[index]}.amount`, true)
    },
    [
      values.resellerAgreementData.installmentInfo,
      values.cppoData.tcv,
      setFieldValue,
    ]
  )

  const shouldGenerateButtonBeDisabled = () => {
    if (
      values.resellerAgreementData.tcv &&
      values.resellerAgreementData.tcv > 0 &&
      values.cppoData.tcv &&
      values.cppoData.tcv > 0
    ) {
      return false
    }
    return true
  }

  const monthsToAdd = values?.resellerAgreementData
    ?.contractDurationInMonthsForEndCustomer
    ? values.resellerAgreementData.contractDurationTimeUnit === 'YEARS'
      ? values?.resellerAgreementData?.contractDurationInMonthsForEndCustomer *
        12
      : values?.resellerAgreementData?.contractDurationInMonthsForEndCustomer
    : 1

  const baseDate = new Date().toISOString().split('T')[0]
  const computedDate = baseDate
    ? addMonthsToDate(baseDate, monthsToAdd)
    : undefined

  return (
    <div
      className={classes.installmentsWrapper}
      data-testid={'installment-section'}
    >
      {addButtonName === 'Add more installments' && (
        <>
          {sectionDescription}
          <div className={classes.sectionHeading}>{sectionHeading}</div>
          <div className={classes.radioSelection}>
            <RadioButtonsV2
              checkedValue={{
                key: checkedValue,
                value: checkedValue,
              }}
              name="installments.flexiblePaymentType"
              radioButtonMappings={scheduleTypeOptions}
              handleChange={handleSelection}
              gapBetween="20px"
            />
          </div>
          {values.cppoData.flexiblePaymentType === ScheduleType.MONTHLY && (
            <NoteV2 description={awsMonthlyInstallmentsBannerMessage} />
          )}
          {!(values.cppoData.flexiblePaymentType === ScheduleType.CUSTOM) && (
            <div className={classes.generateRowRoot}>
              <div
                className={classes.rowResource}
                key={`resellerAgreementData-${randomKey}-paymentDate`}
              >
                <DatePickerV2
                  format={labraUIDateFormatForm}
                  views={['year', 'month', 'day']}
                  size="small"
                  field={{
                    title: 'Select date for first installment',
                    humanReadableKey: 'Select date for first installment',
                    placeHolder: 'Select date for first installment',
                    id: 'resellerAgreementData.firstInstallmentDate',
                    isRequired: false,
                    isReadOnly: disabled,
                    isCustom: false,
                    defaultValue: '',
                    dataType: 'date',
                    minDate: newDate,
                    ...(computedDate ? { maxDate: computedDate } : {}),
                  }}
                  fieldId={'resellerAgreementData.firstInstallmentDate'}
                  formik={
                    formik as unknown as FormikProps<Record<string, unknown>>
                  }
                  showErrorHelper={false}
                />
              </div>

              <div className={classes.generateBtn} key={'generateInstallments'}>
                <ButtonV2
                  styleType="outlined"
                  onClick={generateInstallments}
                  data-testid={'save-as-draft'}
                  disabled={shouldGenerateButtonBeDisabled() || disabled}
                >
                  {'Generate installments'}
                </ButtonV2>
              </div>
            </div>
          )}
        </>
      )}
      <div className={clsx(classes.root, classes.elementsGap)}>
        {subHeading}

        <div className={classes.titleWraper}>
          {customColumns?.map((customColumnItem, customColumnItemIndex) => (
            <Typography
              className={clsx(
                classes.sectionText,
                !customColumnItemIndex
                  ? classes.textAlignLeft
                  : classes.textAlignRight
              )}
              style={{ width: `${columnDivision}%` }}
            >
              {customColumnItem?.header}
            </Typography>
          ))}
          <Typography
            className={clsx(
              classes.sectionText,
              hasCustomColumn ? classes.textAlignRight : ''
            )}
            style={{ width: `${columnDivision}%` }}
          >
            Invoice date
          </Typography>
          <Typography
            className={clsx(
              classes.sectionText,
              hasCustomColumn ? classes.textAlignRight : ''
            )}
            style={{ width: `${columnDivision}%` }}
          >
            Price to ISV
          </Typography>
          {!disabled && (
            <Typography
              className={clsx(
                classes.sectionText,
                hasCustomColumn ? classes.textAlignRight : ''
              )}
              style={{ width: `${columnDivision}%` }}
            >
              Price from Buyer
            </Typography>
          )}
          {showDelete && <div style={{ width: '2%' }}></div>}
        </div>
        <div className={classes.border}></div>
        <FieldArray
          name="resellerAgreementData.installmentInfo"
          key="resellerAgreementData.installmentInfo"
        >
          {({ push, remove }) => {
            const handlePush = (item: any) => {
              push(item)
              const updatedCppo = [...values.cppoData.installmentInfo, item]
              setFieldValue('cppoData.installmentInfo', updatedCppo)
            }

            const handleRemove = (index: number) => {
              remove(index)
              const updatedCppo = values.cppoData.installmentInfo.filter(
                (_, i) => i !== index
              )
              setFieldValue('cppoData.installmentInfo', updatedCppo)
            }
            return (
              <>
                {values.resellerAgreementData.installmentInfo.map(
                  (installment, index) => {
                    return (
                      <>
                        <div
                          className={classes.resourceRow}
                          data-testid="installments-row"
                        >
                          <div
                            style={{ width: `${columnDivision}%` }}
                            key={`resellerAgreementData.installmentInfo-${randomKey}-${index}-datePicker`}
                          >
                            <DatePickerV2
                              format={labraUIDateFormatForm}
                              views={['year', 'month', 'day']}
                              size="small"
                              handleDateChange={async date => {
                                await setFieldValue(
                                  `resellerAgreementData.installmentInfo[${index}].paymentDate`,
                                  date
                                )
                                await setFieldValue(
                                  `cppoData.installmentInfo[${index}].paymentDate`,
                                  date
                                )
                              }}
                              field={{
                                title: 'Select a date',
                                humanReadableKey: 'Select a date',
                                placeHolder: 'Select a date',
                                id: `resellerAgreementData.installmentInfo[${index}].paymentDate`,
                                isRequired: false,
                                isReadOnly: disabled,
                                isCustom: false,
                                defaultValue:
                                  values.resellerAgreementData.installmentInfo[
                                    index
                                  ].paymentDate,
                                dataType: 'date',
                                minDate: newDate,
                                ...(computedDate
                                  ? { maxDate: computedDate }
                                  : {}),
                              }}
                              fieldId={`resellerAgreementData.installmentInfo.${[index]}.paymentDate`}
                              formik={
                                formik as unknown as FormikProps<
                                  Record<string, unknown>
                                >
                              }
                              showErrorHelper={
                                !!(
                                  !!get(
                                    touched,
                                    `resellerAgreementData.installmentInfo.${[index]}.paymentDate`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.installmentInfo.${[index]}.paymentDate`,
                                    ''
                                  )
                                )
                              }
                              errorHelperText={
                                (!!get(
                                  touched,
                                  `resellerAgreementData.installmentInfo.${[index]}.paymentDate`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.installmentInfo.${[index]}.paymentDate`,
                                    ''
                                  )) ||
                                ''
                              }
                            />
                          </div>
                          <div
                            style={{ width: `${columnDivision}%` }}
                            key={`resellerAgreementData.installmentInfo-${randomKey}-${index}-amount`}
                          >
                            <TextInputV4
                              startAdornment={
                                <InputStartAdornment text={currencySymbol} />
                              }
                              alignRight
                              keyName={`resellerAgreementData.installmentInfo.${[index]}.amount`}
                              title="Enter amount"
                              type="number"
                              placeHolder=""
                              value={
                                values.resellerAgreementData.installmentInfo[
                                  index
                                ]?.amount
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `resellerAgreementData.installmentInfo.${[index]}.amount`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.installmentInfo.${[index]}.amount`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `resellerAgreementData.installmentInfo.${[index]}.amount`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.installmentInfo.${[index]}.amount`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={event =>
                                handleChangeAmount(event, index)
                              }
                              isReadOnly={disabled}
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>

                          {!disabled && (
                            <div
                              style={{ width: `${columnDivision}%` }}
                              key={`cppoData.installmentInfo-${randomKey}-${index}-buyer-amount`}
                            >
                              <TextInputV4
                                startAdornment={
                                  <InputStartAdornment text={currencySymbol} />
                                }
                                alignRight
                                keyName={`cppoData.installmentInfo.${[index]}.amount`}
                                title="Enter amount"
                                type="number"
                                placeHolder=""
                                value={
                                  values.cppoData.installmentInfo[index]?.amount
                                }
                                isError={
                                  !!(
                                    get(
                                      touched,
                                      `cppoData.installmentInfo.${[index]}.amount`,
                                      false
                                    ) &&
                                    get(
                                      errors,
                                      `cppoData.installmentInfo.${[index]}.amount`,
                                      false
                                    )
                                  )
                                }
                                errorText={
                                  (!!get(
                                    touched,
                                    `cppoData.installmentInfo.${[index]}.amount`,
                                    false
                                  ) &&
                                    get(
                                      errors,
                                      `cppoData.installmentInfo.${[index]}.amount`,
                                      ''
                                    )) ||
                                  ''
                                }
                                onChange={event =>
                                  handleChangeBuyerAmount(event, index)
                                }
                                isReadOnly={disabled}
                                isRequired={false}
                                showSpinner={false}
                                onBlur={handleBlur}
                                keyToPrevent={['+', '-', 'e']}
                              />
                            </div>
                          )}

                          {showDelete ? (
                            <div className={classes.trashIcon}>
                              <FontAwesomeIcon
                                icon={faTrash}
                                style={{ width: '18px', height: '20px' }}
                                data-testid="trash-installments"
                                onClick={() => handleRemove(index)}
                              />
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                        {index !==
                          values.resellerAgreementData.installmentInfo.length -
                            1 && <div className={classes.border}></div>}
                      </>
                    )
                  }
                )}

                {installmentsLength <= installmentsLimit - 1 &&
                (addButtonName === 'Add more installments'
                  ? values.cppoData.flexiblePaymentType === ScheduleType.CUSTOM
                  : true) ? (
                  <div>
                    {installmentsLength > 0 && (
                      <div
                        className={classes.border}
                        style={{ marginBottom: '20px' }}
                      ></div>
                    )}
                    <ButtonV2
                      className={classes.widthButton}
                      styleType="link"
                      onClick={() => {
                        handlePush(emptyResourse)
                      }}
                      data-testid="add-a-installments-button"
                      smallVariant
                    >
                      {addButtonName}
                    </ButtonV2>
                    <Typography className={classes.buttonSubText}>
                      You can add{' '}
                      {installmentsLimit -
                        (installmentsLength === 0
                          ? 1
                          : installmentsLength)}{' '}
                      more {buttonCounterName}.
                    </Typography>
                  </div>
                ) : (
                  <></>
                )}
              </>
            )
          }}
        </FieldArray>
      </div>
      {installmentsLength > 0 && (
        <div className={clsx(classes.root, classes.metricsRow)}>
          <div className={classes.metricItem} data-testid="total-price">
            <Typography className={classes.sectionText}>
              Total amount
            </Typography>
            <Typography className={classes.totalText} data-testid="price">
              {currencySymbol}
              {values.resellerAgreementData.installmentInfo.reduce(
                (acc, curr) => acc + (Number(curr.amount) || 0),
                0
              )}
            </Typography>
          </div>
          <div className={classes.metricItem} data-testid="installment-count">
            <Typography className={classes.sectionText}>
              Number of installments
            </Typography>
            <Typography
              className={classes.totalText}
              data-testid="installment-count-value"
            >
              {installmentsLength}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export const addOneDayToDate = (dateStr: string) =>
  DateTime.fromISO(dateStr).plus({ days: 1 }).toISODate()

export const addMonthsToDate = (dateStr: string, months: number) =>
  DateTime.fromISO(dateStr).plus({ months }).minus({ days: 1 }).toISODate()
